

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DynamicLookupItem, TaxCondition, TaxGroup, TaxGroupLine, TaxRule, TaxService } from '../../../core/services/tax.service';
import { forkJoin } from 'rxjs';

export interface OrganisationLookup {
      id: string;
      name: string;
}
@Component({
      selector: 'app-tax-dashboard',
      standalone: true,
      imports: [CommonModule, FormsModule, ReactiveFormsModule],
      templateUrl: './tax-group.component.html'
})
export class TaxGroupComponent implements OnInit {
      // Master Datasets
      taxRules: TaxRule[] = [];
      taxGroups: TaxGroup[] = [];
      organisations: OrganisationLookup[] = [];

      // Dropdown Lookup Mocks
      // customerGroups = [{ id: 'cg-001', name: 'Wholesale Tier 1' }, { id: 'cg-002', name: 'Retail Direct' }];
      // itemGroups = [{ id: 'ig-001', name: 'Electronics Raw Material' }, { id: 'ig-002', name: 'Finished Machinery' }];
      // states = [{ id: 'st-01', name: 'Texas HQ' }, { id: 'st-02', name: 'California Hub' }];
      // countries = [{ id: 'us', name: 'United States' }, { id: 'ca', name: 'Canada' }];
      // currencies = [{ id: 'usd', name: 'USD ($)' }, { id: 'eur', name: 'EUR (€)' }];
      // fiscalPositions = [{ id: 'fp-001', name: 'Intra-State Exempt' }, { id: 'fp-002', name: 'Standard Commercial' }];
      customerGroups: DynamicLookupItem[] = [];
      itemGroups: DynamicLookupItem[] = [];
      states: DynamicLookupItem[] = [];
      countries: DynamicLookupItem[] = [];
      currencies: DynamicLookupItem[] = [];
      fiscalPositions: DynamicLookupItem[] = [];
      // UI Modal Controls
      activeModal: 'rule' | 'group' | null = null;
      isEdit = false;
      selectedId: string | null = null;

      // Forms
      ruleForm!: FormGroup;
      groupForm!: FormGroup;

      constructor(
            private fb: FormBuilder,
            private taxService: TaxService,
            private http: HttpClient
      ) { }

      ngOnInit(): void {
            this.buildForms();
            this.refreshWorkspace();
            this.loadOrganisations();
       
            this.loadAllDropdownsFromApi();
      }

      private loadOrganisations(): void {
            this.http.get<OrganisationLookup[]>('https://localhost:7056/api/Permission/organisations').subscribe({
                  next: (res) => this.organisations = res,
                  error: (err) => console.error('Failed to resolve permissions organization lookup records', err)
            });
      }
      // 2. Load all data from API simultaneously using forkJoin
      loadAllDropdownsFromApi() {
            forkJoin({
                 
                  customers: this.taxService.getDynamicDropdown('customerGroups'),
                  items: this.taxService.getDynamicDropdown('itemGroups'),
                  states: this.taxService.getDynamicDropdown('states'),
                  countries: this.taxService.getDynamicDropdown('countries'),
                  currencies: this.taxService.getDynamicDropdown('currencies'),
                  fiscal: this.taxService.getDynamicDropdown('fiscalPositions')
            }).subscribe({
                  next: (results) => {
                        // Assign API responses to component variables
                     
                        this.customerGroups = results.customers;
                        this.itemGroups = results.items;
                        this.states = results.states;
                        this.countries = results.countries;
                        this.currencies = results.currencies;
                        this.fiscalPositions = results.fiscal;
                  },
                  error: (err) => {
                        console.error('Failed to load dropdown API data', err);
                  }
            });
      }

      private buildForms(): void {
            // 1. Tax Rule Architecture Form Setup
            this.ruleForm = this.fb.group({
                  orgId: [null],
                  customerGroupId: [null],
                  itemGroupId: [null],
                  warehouseStateId: [null],
                  customerStateId: [null],
                  countryId: [null],
                  currencyId: [null],
                  fiscalPositionId: [null],
                  taxGroupId: [null, Validators.required],
                  transactionType: ['Sales', Validators.required],
                  priority: [10, [Validators.required, Validators.min(1)]],
                  isActive: [true],
                  conditions: this.fb.array([])
            });

            // 2. Tax Group Architecture Form Setup
            this.groupForm = this.fb.group({
                  orgId: [null],
                  code: ['', Validators.required],
                  name: ['', Validators.required],
                  taxType: ['GST', Validators.required],
                  isActive: [true],
                  countryCode: [null],
                  transactionType: ['Sales'],
                  isInclusive: [false],
                  fiscalPositionCode: [null],
                  priorityNo: [1, [Validators.required, Validators.min(1)]],
                  lines: this.fb.array([])
            });
      }

      refreshWorkspace(): void {
            this.taxService.getAllRules().subscribe(data => this.taxRules = data);
            this.taxService.getAllGroups().subscribe(data => this.taxGroups = data);
      }

      // ==========================================
      // DYNAMIC RULE CONDITIONS ARRAY WORKSPACE
      // ==========================================
      get ruleConditions(): FormArray { return this.ruleForm.get('conditions') as FormArray; }

      addRuleCondition(data?: TaxCondition): void {
            this.ruleConditions.push(this.fb.group({
                  id: [data?.id || null],
                  fieldName: [data?.fieldName || '', Validators.required],
                  operator: [data?.operator || '=', Validators.required],
                  fieldValue: [data?.fieldValue || '', Validators.required]
            }));
      }

      removeRuleCondition(idx: number): void { this.ruleConditions.removeAt(idx); }

      // ==========================================
      // DYNAMIC GROUP BREAKDOWN LINES ARRAY WORKSPACE
      // ==========================================
      get groupLines(): FormArray { return this.groupForm.get('lines') as FormArray; }

      addGroupLine(data?: TaxGroupLine): void {
            this.groupLines.push(this.fb.group({
                  id: [data?.id || null],
                  taxGroupId: [data?.taxGroupId || null],
                  taxCode: [data?.taxCode || '', Validators.required],
                  taxName: [data?.taxName || '', Validators.required],
                  rate: [data?.rate || 0, [Validators.required, Validators.min(0)]],
                  calculationMethod: [data?.calculationMethod || 'percentage', Validators.required],
                  sequenceNo: [data?.sequenceNo || this.groupLines.length + 1, Validators.required],
                  isCompound: [data?.isCompound || false]
            }));
      }

      removeGroupLine(idx: number): void { this.groupLines.removeAt(idx); }

      // ==========================================
      // VIEW TRIGGERS & MUTATIONS
      // ==========================================
      openRuleModal(rule?: TaxRule): void {
            this.activeModal = 'rule';
            this.isEdit = !!rule;
            this.selectedId = rule?.id || null;
            this.ruleConditions.clear();

            if (rule) {
                  this.ruleForm.patchValue(rule);
                  rule.conditions?.forEach(c => this.addRuleCondition(c));
            } else {
                  this.ruleForm.reset({ transactionType: 'Sales', priority: 10, isActive: true });
            }
      }

      openGroupModal(group?: TaxGroup): void {
            this.activeModal = 'group';
            this.isEdit = !!group;
            this.selectedId = group?.id || null;
            this.groupLines.clear();

            if (group) {
                  this.groupForm.patchValue(group);
                  group.lines?.forEach(l => this.addGroupLine(l));
            } else {
                  this.groupForm.reset({ taxType: 'GST', transactionType: 'Sales', isInclusive: false, priorityNo: 1, isActive: true });
            }
      }

      closeModal(): void {
            this.activeModal = null;
            this.selectedId = null;
      }

      getGroupName(id: string): string {
            return this.taxGroups.find(g => g.id === id)?.name || id;
      }

      // ==========================================
      // PERSISTENCE SUBMISSIONS
      // ==========================================
      submitRule(): void {
            if (this.ruleForm.invalid) return;
            const data = this.ruleForm.value;

            if (this.isEdit && this.selectedId) {
                  this.taxService.updateRule(this.selectedId, data).subscribe(() => { this.refreshWorkspace(); this.closeModal(); });
            } else {
                  this.taxService.createRule(data).subscribe(() => { this.refreshWorkspace(); this.closeModal(); });
            }
      }

      submitGroup(): void {
            if (this.groupForm.invalid) return;
            const data = this.groupForm.value;

            if (this.isEdit && this.selectedId) {
                  this.taxService.updateGroup(this.selectedId, data).subscribe(() => { this.refreshWorkspace(); this.closeModal(); });
            } else {
                  this.taxService.createGroup(data).subscribe(() => { this.refreshWorkspace(); this.closeModal(); });
            }
      }

      deleteRule(id: string): void {
            if (confirm('Delete selected tax calculation rule profile?')) {
                  this.taxService.deleteRule(id).subscribe(() => this.refreshWorkspace());
            }
      }

      deleteGroup(id: string): void {
            if (confirm('Delete selected tax group configuration setup?')) {
                  this.taxService.deleteGroup(id).subscribe(() => this.refreshWorkspace());
            }
      }

}